import { forwardRef, useCallback, useEffect, useState } from 'react'
import { Input } from '@klear/ui/components/input'
import { Label } from '@klear/ui/components/label'
import { cn } from '@klear/ui/lib/utils'
import { MAX_BASIS_POINTS, type BasisPoints } from '@klear/basis-points'
import { parseBasisPoints, basisPointsToPercentString } from '@klear/basis-points/boundary'
import type { LucideIcon } from 'lucide-react'

export interface BasisPointsInputProps {
  value: BasisPoints | undefined
  onChange: (value: BasisPoints | undefined) => void
  max?: number
  label?: string
  placeholder?: string
  disabled?: boolean
  className?: string
  icon?: LucideIcon
  helperText?: string
  errorMessage?: string
  onBlur?: () => void
  displayDigits?: number
}

function formatDisplay(value: BasisPoints | undefined, digits: number): string {
  if (value == null) return ''
  return basisPointsToPercentString(value, digits)
}

const BasisPointsInput = forwardRef<HTMLInputElement, BasisPointsInputProps>(
  (
    {
      value,
      onChange,
      max = MAX_BASIS_POINTS,
      label,
      placeholder = '0.00',
      disabled = false,
      className,
      icon: Icon,
      helperText,
      errorMessage,
      onBlur,
      displayDigits = 2
    },
    ref
  ) => {
    const [draft, setDraft] = useState<string>(() => formatDisplay(value, displayDigits))
    const [isFocused, setIsFocused] = useState(false)

    useEffect(() => {
      if (isFocused) return
      setDraft(formatDisplay(value, displayDigits))
    }, [value, displayDigits, isFocused])

    const handleInput = useCallback(
      (e: React.FormEvent<HTMLInputElement>) => {
        const raw = e.currentTarget.value
        setDraft(raw)
        const parsed = parseBasisPoints(raw, max)
        onChange(parsed)
      },
      [onChange, max]
    )

    const handleFocus = useCallback(() => {
      setIsFocused(true)
    }, [])

    const handleBlur = useCallback(() => {
      setIsFocused(false)
      const parsed = parseBasisPoints(draft, max)
      onChange(parsed)
      setDraft(formatDisplay(parsed, displayDigits))
      onBlur?.()
    }, [draft, displayDigits, max, onBlur, onChange])

    return (
      <div className={cn('space-y-2', className)}>
        {label && (
          <Label className="flex items-center gap-2">
            {Icon && <Icon className="h-4 w-4" />}
            {label}
          </Label>
        )}
        <Input
          ref={ref}
          type="text"
          inputMode="decimal"
          placeholder={placeholder}
          disabled={disabled}
          value={draft}
          onInput={handleInput}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
        {helperText && <p className="text-sm text-muted-foreground">{helperText}</p>}
      </div>
    )
  }
)

BasisPointsInput.displayName = 'BasisPointsInput'
export { BasisPointsInput }
